/**
 * Dynamic API Configuration
 * Automatically detects the current domain and constructs the appropriate backend URL
 */

/**
 * Get the dynamic backend URL based on current environment and domain
 * @returns {string} The backend URL
 */
export const getBackendUrl = () => {
  // Check if we have an explicit backend URL set
  const explicitBackendUrl = process.env.REACT_APP_BACKEND_URL;
  
  // If explicitly set and not empty, use it
  if (explicitBackendUrl && explicitBackendUrl.trim() !== '') {
    const cleanUrl = explicitBackendUrl.endsWith('/') ? explicitBackendUrl.slice(0, -1) : explicitBackendUrl;
    console.log('Using explicit backend URL:', cleanUrl);
    return cleanUrl;
  }
  
  // For development, check if we're running on a known dev port
  if (process.env.NODE_ENV === 'development') {
    const currentHost = window.location.hostname;
    const currentProtocol = window.location.protocol;
    
    // If running on localhost:3000 (typical React dev), point to localhost:8001
    if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
      const devUrl = `${currentProtocol}//${currentHost}:8001`;
      console.log('Using development backend URL:', devUrl);
      return devUrl;
    }
  }
  
  // For production or when served from the same domain, use current origin
  // This works when frontend and backend are served from the same domain
  const currentOrigin = window.location.origin;
  console.log('Using current origin as backend URL:', currentOrigin);
  return currentOrigin;
};

/**
 * Construct full API endpoint URL
 * @param {string} endpoint - The API endpoint path (should start with /api/)
 * @param {boolean} isStaticFile - Whether this is a static file URL (doesn't need /api prefix)
 * @returns {string} Full URL to the API endpoint
 */
export const getApiUrl = (endpoint, isStaticFile = false) => {
  const backendUrl = getBackendUrl();
  
  // If it's a static file, don't add /api prefix
  if (isStaticFile) {
    // Ensure endpoint starts with /
    if (!endpoint.startsWith('/')) {
      endpoint = '/' + endpoint;
    }
    return `${backendUrl}${endpoint}`;
  }
  
  // Ensure endpoint starts with /api
  if (!endpoint.startsWith('/api')) {
    endpoint = '/api' + (endpoint.startsWith('/') ? endpoint : '/' + endpoint);
  }
  
  return `${backendUrl}${endpoint}`;
};

/**
 * Get the current backend URL for display purposes
 * @returns {string} The current backend URL
 */
export const getCurrentBackendUrl = () => {
  return getBackendUrl();
};

// Export the backend URL (for backward compatibility)
export const BACKEND_URL = getBackendUrl();

// Default export
export default {
  getBackendUrl,
  getApiUrl,
  getCurrentBackendUrl,
  BACKEND_URL
};